import {
  BaseTextSearchCriteriaLabelModifier,
  baseTextSearchCriteria,
} from '@/features/project/_models/filter-base-data';
import { PS_MAX_KEYWORD_SEARCH_LENGTH } from '@/features/project/_utils/constants';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import { isPS } from '@/utils';
import type { fundSheetTypeEnum, sheetTypeEnum } from '@/utils/enum';
import { useEffect, useMemo } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type z from 'zod';

type BaseTextSearchCriteriaType = z.infer<typeof baseTextSearchCriteria>;

export const useGenerateKeywordHintText = ({
  isRSAndAdministrativeReformTeamEvaluation,
  sheetType,
  fundSheetType,
}: {
  isRSAndAdministrativeReformTeamEvaluation: boolean;
  sheetType: z.infer<typeof sheetTypeEnum>;
  fundSheetType: z.infer<typeof fundSheetTypeEnum> | null;
}) => {
  const { t } = useTranslation();

  const searchTarget = useMemo(() => {
    const targets = (() => {
      if (sheetType === 'KS') {
        if (fundSheetType === 'standard') {
          return [
            'fund_name',
            'fund_owner',
            'name',
            'purpose',
            'overview',
            'task',
          ] satisfies BaseTextSearchCriteriaType[];
        }
        if (fundSheetType === 'investment') {
          return ['name', 'overview'] satisfies BaseTextSearchCriteriaType[];
        }
        if (fundSheetType === 'local-government') {
          return [
            'fund_name',
            'name',
            'overview',
          ] satisfies BaseTextSearchCriteriaType[];
        }
      }

      return isRSAndAdministrativeReformTeamEvaluation
        ? ['name']
        : baseTextSearchCriteria.options.filter(
            (v) => v !== 'fund_name' && v !== 'fund_owner',
          );
    })();

    return targets
      .map((value) => `「${BaseTextSearchCriteriaLabelModifier[value]}」`)
      .join('');
  }, [sheetType, fundSheetType, isRSAndAdministrativeReformTeamEvaluation]);

  const hintText = useMemo(() => {
    const prefixText = isRSAndAdministrativeReformTeamEvaluation
      ? ''
      : t('hint-search-keyword-prefix-text', {
          target: searchTarget,
        });

    const environmentText = isPS()
      ? `${t('hint-search-keyword-prefix-public', {
          limit: PS_MAX_KEYWORD_SEARCH_LENGTH,
        })}\n`
      : isRSAndAdministrativeReformTeamEvaluation
        ? ''
        : '\n';
    return t('hint-search-keyword', {
      prefix: `${prefixText}${environmentText}`,
    });
  }, [isRSAndAdministrativeReformTeamEvaluation, searchTarget, t]);

  return hintText;
};

// testのために分割し、パラメータは外部から受け取るようにする。
export const useKeywordFieldInitializeEffect = ({
  skipAdministrativeReformTeamEvaluation,
  type,
  setProjectSearchValue,
}: {
  skipAdministrativeReformTeamEvaluation: 'true' | 'false' | undefined;
  type: 'project' | 'group' | 'analysis' | undefined;
  setProjectSearchValue: UseFormSetValue<ProjectSearchFormType>;
}) => {
  useEffect(() => {
    if (
      skipAdministrativeReformTeamEvaluation === 'true' &&
      type !== 'analysis'
    ) {
      setProjectSearchValue('isSearchTargetProjectName', true);
    }
  }, [skipAdministrativeReformTeamEvaluation, setProjectSearchValue, type]);
};
